import useSWRMutation, { type SWRMutationConfiguration } from 'swr/mutation';
import denApi from '@/lib/api/denApi';
import { type ExecuteAction } from '@mls-wallet/shared-types';

/**
 * Response from execute endpoint.
 * Contains the guardianTransactionId for polling execution status.
 */
interface ExecuteTransactionResponse {
  guardianTransactionId: string;
}

type ExecuteAccountTransactionArg = {
  transactionId: string;
  action: ExecuteAction;
};

const fetcher = async (
  _key: string,
  { arg }: { arg: ExecuteAccountTransactionArg }
): Promise<ExecuteTransactionResponse> => {
  const { transactionId, action } = arg;
  return await denApi.post<
    ExecuteTransactionResponse,
    { action: ExecuteAction }
  >(`transactions/${encodeURIComponent(transactionId)}/execute`, { action });
};

/**
 * Hook to execute an account transaction that has reached its approval threshold.
 *
 * After calling executeTransaction, the response contains a guardianTransactionId
 * that should be used with useGuardianTransactionStatus to poll execution status.
 */
export function useExecuteAccountTransaction(
  swrOptions?: SWRMutationConfiguration<
    ExecuteTransactionResponse,
    Error,
    string,
    ExecuteAccountTransactionArg
  >
) {
  const { trigger, isMutating, error } = useSWRMutation<
    ExecuteTransactionResponse,
    Error,
    string,
    ExecuteAccountTransactionArg
  >('transactions/execute', fetcher, swrOptions);

  return { executeTransaction: trigger, isLoading: isMutating, error };
}
